import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";

import { StatusCode } from "api/protocols";
import {
  CustomerMembership,
  GetCustomerMemberships,
  IGetCustomerMemberships,
} from "api/rpc/2024-04/facilityAdmin/customer/membership";

import { useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Search from "components/search/Search";

import DataTable from "../../../../../components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";

import "./soldMemberships.scss";

const tableColumns = (t: TFunction<"translation", undefined>) => {
  return [
    { label: t("Customer"), width: "auto" }, // full_name + email
    { label: t("Home Facility"), width: "auto" },
    { label: t("Membership"), width: "auto" },
    { label: t("Member Number"), width: "auto" },
    { label: t("Season"), width: "auto" },
    { label: t("Status"), width: "auto" },
    { label: t("Expiry"), width: "auto" },
  ];
};

const TABLE_LIMIT = 50;
export default function MembershipsSold() {
  const { t } = useTranslation();

  const { facilityStore } = useAppSelector(store => store);

  const [membershipList, setMembershipList] = useState<CustomerMembership[]>(undefined);
  const [filters, setFilters] = useState<Partial<IGetCustomerMemberships>>({
    search: "",
    offset: 0,
    limit: TABLE_LIMIT,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadMembershipList(source.token);
    return () => {
      source.cancel();
    };
  }, [filters]);

  async function loadMembershipList(token?: CancelToken) {
    if (membershipList !== undefined) {
      setMembershipList(undefined);
    }
    const res = await GetCustomerMemberships({ ...filters, limit: TABLE_LIMIT }, false, token);

    if (token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      setMembershipList([]);
      return;
    }

    setMembershipList(res.data as CustomerMembership[]);
  }

  return (
    <Page className="sold-memberships-page" title={t("Sold Memberships")}>
      <div className="sold-memberships-search">
        <Search
          historyKey={"facility-customer-sold-memberships"}
          searchCallback={(searchValue: string) =>
            searchValue !== filters.search ? setFilters(prev => ({ ...prev, search: searchValue })) : null
          }
          placeholder={t("Search by customer or membership number...")}
        />
      </div>
      <div className="data-table-scroll">
        <DataTable
          columns={tableColumns(t)}
          footer={{
            tableLimit: filters.limit,
            tableOffset: filters.offset,
            disableNextOffset: !membershipList || !(membershipList?.length === filters.limit),
            handleTableOffset: direction =>
              setFilters(prev => ({
                ...prev,
                offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
              })),
          }}
          loading={membershipList === undefined}
        >
          {membershipList?.map(membership => (
            <tr key={membership.id}>
              <td>
                <div>{membership.customer?.full_name}</div>
                <div>{membership.customer?.email}</div>
              </td>
              <td>
                {facilityStore.facilities.find(obj => obj.id === membership.home_facility_id) ? (
                  <>{facilityStore.facilities.find(obj => obj.id === membership.home_facility_id).long_name}</>
                ) : (
                  <p className="field-unavailable">{t("N/A")}</p>
                )}
              </td>
              <td>
                <div>{membership.membership_title}</div>
                <div>{membership.membership_subtitle}</div>
              </td>

              <td>{membership.customer?.member_code ? membership.customer.member_code : ""}</td>
              <td>{membership.season && <Badge type="primary">{membership.season}</Badge>}</td>
              <td>
                <Badge type={generateBadgeType(membership.status)}>{membership.status_readable}</Badge>
              </td>
              <td>
                {membership.expiry ? (
                  moment(membership.expiry).format("MMMM Do, YYYY")
                ) : (
                  <p className="field-unavailable">{t("N/A")}</p>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
    </Page>
  );
}

/** Return badge type from membership.status
 *   -  Easily adjust badge styling in 1 place going forward
 */
function generateBadgeType(status: "active" | "expired" | "disabled") {
  switch (status) {
    case "active":
      return "success";
    case "expired":
      return "warning";
    case "disabled":
      return "error";
    default:
      return "primary";
  }
}
